<template>
  <div class="bancai-details-page">
    <!-- 加载状态 -->
    <div v-if="loading" class="loading-container">
      <div class="loading-spinner"></div>
      <p>正在加载产品信息...</p>
    </div>
    
    <!-- 产品参数区域 -->
    <section class="product-section">
      <div class="container">

        
        <div class="product-layout">
          <!-- 左侧款式图片 -->
          <div class="styles-gallery" :key="productData.id + '-' + (productData.styles ? productData.styles.length : 0)" @touchstart="onTouchStart" @touchmove="onTouchMove" @touchend="onTouchEnd">
            <!-- <h3 class="section-title">产品款式</h3> -->
            <div class="styles-list" ref="stylesListRef">
              <div 
                v-for="(style, index) in productData.styles" 
                :key="productData.id + '-' + (style.id || style.image || style.name || index)"
                class="style-item"
                :class="{ active: currentStyleIndex === index }"
                @click="selectStyle(index)"
              >
                <img :src="style.image" :alt="style.name" :key="(style.image || '') + '-' + index" />
                <span class="style-name">{{ style.name }}</span>
              </div>
            </div>
            <div class="nav-arrows-container" v-if="(productData.styles && productData.styles.length > 1)">
              <button class="nav-arrow prev" aria-label="上一张" @click.stop="prevStyle">‹</button>
              <button class="nav-arrow next" aria-label="下一张" @click.stop="nextStyle">›</button>
            </div>
          </div>

          <!-- 中间主图 -->
          <div class="main-image">
            <img 
              :src="currentMainImage" 
              :alt="productData.name" 
              :key="currentMainImage"
              @click="showImagePreview"
            />
            <div class="image-overlay" @click="showImagePreview">
              <!-- <i class="zoom-icon">🔍</i> -->
            </div>
          </div>

          <!-- 右侧产品参数 -->
          <div class="product-info">
            <div class="product-summary">
              <div class="product-name">{{ productData.name }}</div>
              <div class="product-origin">
                <span class="origin-tag">{{ productData.originTag || '100%意大利进口岩板' }}</span>
              </div>
              <p class="product-description">{{ productData.description }}</p>
            </div>
            
            <div class="specifications">
              <div class="spec-title"></div>
              <div class="spec-list">
                <div class="spec-item">
                  <span class="spec-label">尺寸：</span>
                  <span class="spec-value">{{ productData.specifications.size }}</span>
                </div>
                <!-- <div class="spec-item">
                  <span class="spec-label">厚度：</span>
                  <span class="spec-value">{{ productData.specifications.thickness }}</span>
                </div> -->
                <div class="spec-item">
                  <span class="spec-label">表面：</span>
                  <span class="spec-value">{{ productData.specifications.surface }}</span>
                </div>
                <div class="spec-item">
                  <span class="spec-label">基材：</span>
                  <span class="spec-value">{{ productData.specifications.substrate }}</span>
                </div>
              </div>
            </div>
            
            <!-- <div class="notes">
              <div class="section-divider"></div>
              <h3 class="notes-title">注意</h3>
              <ol class="notes-list">
                <li v-for="(note, index) in productData.notes" :key="index">
                  {{ note }}
                </li>
              </ol>
            </div> -->
 
            <div class="contact-info">
              <!-- <p class="contact-text">更多产品信息请联系您所在当地经销商</p> -->
              <button class="contact-btn" @click="contactDealer">更多产品信息请联系您所在当地经销商</button>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 应用场景区域 -->
    <section class="applications-section" v-if="productData.applications && productData.applications.length > 0">
      <div class="container">
        <div class="section-title">场景应用</div>
        <div class="applications-grid">
          <div 
            v-for="(app, index) in productData.applications" 
            :key="app.image || app.title || index"
            class="application-item"
            @click="showApplicationPreview(app)"
          >
            <img :src="app.image" :alt="app.title" />
          </div>
        </div>
      </div>
    </section>

    <!-- 相关产品区域 -->
    <section class="related-section" v-if="relatedProducts.length > 0">
      <div class="container">
        <div class="section-title">相关产品</div>
        <div class="related-grid">
          <div 
            v-for="product in relatedProducts" 
            :key="product.id"
            class="related-item"
            @click="goToProduct(product)"
          >
            <div class="related-image">
              <img :src="product.image" :alt="product.name" />
            </div>
            <div class="related-info">
              <div class="related-name">{{ product.name }}</div>
              <!-- <p class="related-spec">{{ product.specification }}</p> -->
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 图片预览模态框 -->
    <div v-if="showPreview" class="image-preview-modal" @click="closePreview">
      <div class="modal-content" @click.stop>
        <div style="margin-top: 30px;">×</div>
        <!-- <button class="close-btn" @click="closePreview">×</button>  -->
        <img :src="previewImage" :alt="previewTitle" />
        <div class="preview-title">{{ previewTitle }}</div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { getBanCai } from '@/api/api'
import { testData } from './testData.js'

const route = useRoute()
const router = useRouter()

// 响应式数据
const productData = ref({
  id: '',
  name: '',
  description: '',
  specifications: {
    size: '',
    thickness: '',
    surface: '',
    substrate: ''
  },
  notes: [],
  styles: [],
  applications: [],
  mainImage: '',
  originTag: ''
})

const relatedProducts = ref([])
const currentStyleIndex = ref(0)
const showPreview = ref(false)
const previewImage = ref('')
const previewTitle = ref('')
const loading = ref(false)
const stylesListRef = ref(null)
const touchStartX = ref(0)
const touchDeltaX = ref(0)

// URL 规范化，兼容相对/绝对路径
const resolveUrl = (url) => {
  if (!url) return ''
  if (typeof url !== 'string') return ''
  if (/^https?:\/\//i.test(url)) return url
  if (url.startsWith('/')) return `${window.location.origin}${url}`
  return `${window.location.origin}/${url}`
}

// 计算属性
const currentMainImage = computed(() => {
  if (productData.value.styles && productData.value.styles.length > 0) {
    return resolveUrl(productData.value.styles[currentStyleIndex.value].image)
  }
  return resolveUrl(productData.value.mainImage)
})

// 方法
const scrollToCurrentStyle = () => {
  requestAnimationFrame(() => {
    const list = stylesListRef.value
    if (!list) return
    const items = list.querySelectorAll('.style-item')
    const el = items[currentStyleIndex.value]
    if (el && el.scrollIntoView) {
      el.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' })
    }
  })
}

const prevStyle = () => {
  const total = productData.value.styles ? productData.value.styles.length : 0
  if (total === 0) return
  currentStyleIndex.value = (currentStyleIndex.value - 1 + total) % total
  scrollToCurrentStyle()
}

const nextStyle = () => {
  const total = productData.value.styles ? productData.value.styles.length : 0
  if (total === 0) return
  currentStyleIndex.value = (currentStyleIndex.value + 1) % total
  scrollToCurrentStyle()
}

const onTouchStart = (e) => {
  if (!e.touches || e.touches.length === 0) return
  touchStartX.value = e.touches[0].clientX
  touchDeltaX.value = 0
}

const onTouchMove = (e) => {
  if (!e.touches || e.touches.length === 0) return
  touchDeltaX.value = e.touches[0].clientX - touchStartX.value
}

const onTouchEnd = () => {
  const threshold = 40
  if (Math.abs(touchDeltaX.value) > threshold) {
    if (touchDeltaX.value < 0) {
      nextStyle()
    } else {
      prevStyle()
    }
  }
  touchStartX.value = 0
  touchDeltaX.value = 0
}

const fetchProductDetails = async () => {
  loading.value = true
  try {
    const productId = route.query.id
    
    // 优先使用测试数据进行调试
    let currentProduct = testData.find(item => item.id == productId)
    
    if (currentProduct) {
      // 使用测试数据
      console.log('使用测试数据:', currentProduct)
      productData.value = {
        id: currentProduct.id,
        name: currentProduct.name,
        description: currentProduct.description,
        specifications: {
          size: currentProduct.size,
          thickness: currentProduct.thickness,
          surface: currentProduct.surface,
          substrate: currentProduct.substrate
        },
        originTag: currentProduct.originTag || '',
        notes: currentProduct.notes,
        styles: (currentProduct.styles || []).map(s => ({ ...s, image: resolveUrl(s.image) })),
        applications: (currentProduct.applications || []).map(a => ({ ...a, image: resolveUrl(a.image) })),
        mainImage: resolveUrl(currentProduct.image),
      }
      
      // 从测试数据中随机选择相关产品（排除当前产品）
      const otherProducts = testData.filter(item => item.id !== currentProduct.id)
      relatedProducts.value = getRandomProducts(otherProducts, 6)
    } else {
      // 如果测试数据中没有找到，尝试从接口获取
      console.log('测试数据中未找到，尝试从接口获取')
      const res = await getBanCai()
      console.log('板材数据:', res)
      
      if (res) {
        const rawCandidate = (res.data || res.body) || res
        const raw = (rawCandidate && typeof rawCandidate === 'object') ? rawCandidate : res
        const allProducts = normalizeItems(raw)
        currentProduct = allProducts.find(item => item.id == productId)
        
        if (currentProduct) {
          // 解析JSON字段
          let styles = []
          let applications = []
          let notes = []
          
          try {
            if (currentProduct.styles) {
              if (typeof currentProduct.styles === 'string') {
                styles = JSON.parse(currentProduct.styles)
              } else if (Array.isArray(currentProduct.styles)) {
                styles = currentProduct.styles
              }
            }
          } catch (e) {
            console.warn('解析styles失败:', e)
            styles = [{ name: '默认款式', image: currentProduct.mainImage || require('@/common/image/allListbanner.png') }]
          }
          
          try {
            if (currentProduct.applications) {
              if (typeof currentProduct.applications === 'string') {
                applications = JSON.parse(currentProduct.applications)
              } else if (Array.isArray(currentProduct.applications)) {
                applications = currentProduct.applications
              }
            }
          } catch (e) {
            console.warn('解析applications失败:', e)
            applications = [{
              title: '默认应用',
              description: '产品应用场景',
              image: currentProduct.mainImage || require('@/common/image/allListbanner.png')
            }]
          }
          
          try {
            if (currentProduct.notes) {
              if (typeof currentProduct.notes === 'string') {
                notes = JSON.parse(currentProduct.notes)
              } else if (Array.isArray(currentProduct.notes)) {
                notes = currentProduct.notes
              }
            }
          } catch (e) {
            console.warn('解析notes失败:', e)
            notes = ['产品注意事项']
          }
          
          // 使用接口数据
          productData.value = {
            id: currentProduct.id,
            name: currentProduct.name || '未命名产品',
            description: currentProduct.description || '产品描述',
            specifications: {
              size: currentProduct.size || '尺寸规格',
              thickness: currentProduct.thickness || '厚度',
              surface: currentProduct.surface || '表面处理',
              substrate: currentProduct.substrate || '基材'
            },
            originTag: currentProduct.originTag || '',
            notes: notes,
            styles: (styles || []).map(s => ({ ...s, image: resolveUrl(s.image) })),
            applications: (applications || []).map(a => ({ ...a, image: resolveUrl(a.image) })),
            mainImage: resolveUrl(currentProduct.mainImage) || require('@/common/image/allListbanner.png')
          }
          
          // 从所有产品中随机选择相关产品（排除当前产品）
          const otherProducts = allProducts.filter(item => item.id !== currentProduct.id)
          relatedProducts.value = getRandomProducts(otherProducts, 6)
        } else {
          // 如果没找到对应ID的产品，使用默认数据
          setDefaultProductData()
        }
      } else {
        setDefaultProductData()
      }
    }
  } catch (error) {
    console.error('获取产品详情失败:', error)
    setDefaultProductData()
  } finally {
    // 每次完成数据加载后，重置状态，确保主图与列表重渲染
    currentStyleIndex.value = 0
    showPreview.value = false
    previewImage.value = ''
    previewTitle.value = ''
    loading.value = false
  }
}

// 数据标准化函数
const normalizeItems = (data) => {
  if (!data) return []
  if (Array.isArray(data)) return data
  if (Array.isArray(data.list)) return data.list
  if (Array.isArray(data.records)) return data.records
  // 适配后端返回 { bancaiList: [...] }
  if (Array.isArray(data.bancaiList)) return data.bancaiList
  return []
}

// 随机选择产品函数
const getRandomProducts = (products, count) => {
  const shuffled = [...products].sort(() => 0.5 - Math.random())
  return shuffled.slice(0, count).map(item => ({
    id: item.id,
    name: item.name,
    image: resolveUrl(item.mainImage || item.image) || require('@/common/image/allListbanner.png'),
    specification: item.specification || item.subtitle || '4个饰面'
  }))
}

// 设置默认产品数据
const setDefaultProductData = () => {

}

const selectStyle = (index) => {
  currentStyleIndex.value = index
}

const showImagePreview = () => {
  previewImage.value = currentMainImage.value
  // 显示当前选中款式的名称，如果没有款式则显示产品名称
  if (productData.value.styles && productData.value.styles.length > 0) {
    previewTitle.value = productData.value.styles[currentStyleIndex.value].name
  } else {
    previewTitle.value = productData.value.name
  }
  showPreview.value = true
}

const showApplicationPreview = (app) => {
  previewImage.value = app.image
  previewTitle.value = app.title
  showPreview.value = true
}

const closePreview = () => {
  showPreview.value = false
}

const contactDealer = () => {
  router.push('/contact')
}

const goToProduct = (product) => {
  router.push({
    path: '/bancaiDetails',
    query: { id: product.id, name: product.name }
  })
}

onMounted(() => {
  console.log('详情页面加载，路由参数:', route.query)
  fetchProductDetails()
})

// 监听路由参数变化
watch(
  () => route.query.id,
  (newId, oldId) => {
    if (newId !== oldId) {
      console.log('路由参数变化，重新获取产品详情:', newId)
      // 先重置局部状态，再拉取新数据，避免残留图片
      currentStyleIndex.value = 0
      showPreview.value = false
      previewImage.value = ''
      previewTitle.value = ''
      fetchProductDetails()
    }
  }
)

// 小屏滑动视图：索引变化时滚动到当前项
watch(currentStyleIndex, () => {
  scrollToCurrentStyle()
})
</script>

<style scoped>
.bancai-details-page {
  width: 100%;
  min-height: 100vh;
  background: #f8f9fa;
  /* font-family: "Lantinghei SC Extralight", "Lantinghei SC Demibold", "Lantinghei SC Heavy", "Lantinghei SC", "PingFang SC", "Microsoft YaHei", "Helvetica Neue", Arial, sans-serif; */
}

/* 加载状态样式 */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  background: #fff;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #007bff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 20px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.loading-container p {
  color: #666;
  font-size: 16px;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

/* 产品区域样式 */
.product-section {
  padding: 60px 0;
  background: #fff;
}

.product-layout {
  display: grid;
  grid-template-columns: 120px 400px 400px;
  row-gap: 0px;
  column-gap: 60px;
  align-items: stretch;
  max-width: 1400px;
  margin: 0 auto;
}

/* 大屏幕时减小左侧图片与主图的间距 */
@media (min-width: 992px) {
  .product-layout {
    column-gap: 20px; /* 减小列间距，让左侧图片靠近主图 */
  }
  
  /* 大屏幕时只增加右侧参数的左边距 */
  .product-info {
    margin-left: 40px;
  }
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin-bottom: 20px;
  text-align: center;
}

/* 左侧款式图片 */
.styles-gallery {
  padding: 0;
}

/* 桌面端：左侧图片区域设置 */
@media (min-width: 992px) {
  .styles-gallery {
    height: 85vh; /* 与主图高度保持一致 */
  }
  
  .styles-list {
    height: 100%;
    overflow: hidden; /* 不滚动，只预留4张图片位置 */
    justify-content: flex-start; /* 图片从顶部开始排列 */
  }
  

}

.styles-list {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.style-item {
  cursor: pointer;
  border: none;
  overflow: hidden;
  transition: all 0.3s ease;
  position: relative;
  padding-top: 200%;
}

/* 大屏幕时直接设置固定高度 */
@media (min-width: 992px) {
  .style-item {
    padding-top: 0;
    height: 200px; /* 设置固定高度，可调整 */
    width: 120px; /* 减小宽度 */
  }
}



.style-item:hover,
.style-item.active {
  border-color: #333;
}

.style-item img {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.style-name {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 8px;
  text-align: center;
  background: rgba(0, 0, 0, 0.4);
  color: #fff;
  font-size: 14px;
  font-weight: 500;
}

/* 中间主图 */
.main-image {
  position: relative;
  border: none;
  overflow: hidden;
  cursor: pointer;
  padding-top: 200%;
}

/* 桌面端：主图高度 = 一个屏幕高度 */
@media (min-width: 992px) {
  .main-image {
    padding-top: 0;
    height: 85vh;
  }
}

.main-image img {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.main-image:hover img {
  transform: scale(1.01);
}

.image-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.main-image:hover .image-overlay {
  opacity: 1;
}

.zoom-icon {
  font-size: 24px;
  color: #fff;
  background: rgba(0, 0, 0, 0.6);
  padding: 8px;
  border-radius: 50%;
}

/* 右侧产品信息 */
.product-info {
  padding: 0;
  height: 100%;
  text-align: left;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
}

.product-name {
  font-size: 36px;
  font-weight: 500;
  /* font-family: "Lantinghei SC Extralight", "Lantinghei SC Demibold", "Lantinghei SC Heavy", "Lantinghei SC", "PingFang SC", "Microsoft YaHei", "Helvetica Neue", Arial, sans-serif; */
  color: #333;
  margin-bottom: 15px;
  line-height: 1.2;
  text-align: left;
}

.product-origin {
  margin-bottom: 20px;
}

.origin-tag {
  color: #333;
  font-size: 16px;
  font-weight: 500;
  /* font-family: "Lantinghei SC Extralight", "Lantinghei SC Demibold", "Lantinghei SC Heavy", "Lantinghei SC", "PingFang SC", "Microsoft YaHei", "Helvetica Neue", Arial, sans-serif; */
}

.product-description {
  color: #666;
  line-height: 1.6;
  margin-bottom: 25px;
  font-size: 14px;
  text-align: left;
}

.specifications {
  margin-bottom: 25px;
}

.spec-title {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin-bottom: 15px;
  border-bottom: 1px solid #333;
  padding-bottom: 5px;
  text-align: left;
}

.spec-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.spec-item {
  display: flex;
  justify-content: flex-start;
  padding: 8px 0;
  align-items: center;
  gap: 0px;
}

.spec-label {
  color: #000;
  font-weight: 500;
  font-size: 14px;
  min-width: 60px;
}

/* 桌面端：缩小标签最小宽度，让数值更靠近标签 */
@media (min-width: 992px) {
  .spec-label {
    min-width: 10px;
  }
}

.spec-value {
  color: #555;
  font-weight: 300;
  font-size: 14px;
}

.notes {
  margin-bottom: 25px;
}

.notes-title {
  font-family: inherit; /* 与 spec-label 字体一致 */
  font-size: 13px; /* 比 spec-label 更小 */
  font-weight: 600; /* 比 spec-label(700) 更轻 */
  color: #333;
  margin-bottom: 15px;
  padding-bottom: 5px;
  text-align: left;
}

.notes-list {
  list-style: decimal;
  padding-left: 20px;
  text-align: left;
}

.notes-list li {
  padding: 6px 0;
  color: #666;
  font-size: 13px;
  line-height: 1.5;
}

.contact-info {
  text-align: right;
  padding-top: 20px;
  margin-top: auto;
}

.contact-text {
  color: #666;
  margin-bottom: 15px;
  font-size: 13px;
  line-height: 1.5;
  text-align: left;
}

.contact-btn {
  background: #544B46; /* 改为指定背景色 */
  color: #fff;
  border: none;
  padding: 12px 24px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  transition: background 0.3s ease;
  margin-left: auto;
  width: 100%;
  white-space: nowrap;
  word-break: keep-all;
}

.contact-btn:hover {
  background: #544B46;
}

/* 默认隐藏箭头容器（仅小屏显示） */
.nav-arrows-container { display: none; }

@media (max-width: 768px) {
  .nav-arrows-container { display: block; }
}

@media (min-width: 992px) {
  .contact-btn { font-size: 18px; }
}

/* 应用场景区域 */
.applications-section {
  padding: 60px 0;
  background: #fff;
}

.section-divider {
  width: 100%;
  height: 1px; /* 统一分割线粗细 */
  background: #333;
  margin-bottom: 40px;
}

.applications-section .section-title {
  text-align: left;
  font-family: inherit;
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin-bottom: 40px;
}

/* 大屏为“场景应用”标题添加冒号 */
.applications-section .section-title::after {
  content: '：';
  margin-left: 2px;
}

.applications-grid {
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
  margin: 0;
  align-items: flex-start;
}

.application-item {
  position: relative;
  border: none;
  overflow: hidden;
  cursor: pointer;
  transition: transform 0.3s ease;
  width: 100%;
  margin: 0;
}

.application-item:hover {
  transform: translateY(-2px);
}

.application-item img {
  width: 100%;
  height: 800px;
  object-fit: cover;
}

/* 相关产品区域 */
.related-section {
  padding: 60px 0;
  background: #f8f9fa;
}

.related-section .section-title {
  text-align: left;
  font-family: inherit;
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin-bottom: 40px;
}

/* 大屏为“相关产品”标题添加冒号 */
.related-section .section-title::after {
  content: '：';
  margin-left: 2px;
}

/* 桌面端：移除加粗与冒号 */
@media (min-width: 992px) {
  .applications-section .section-title,
  .related-section .section-title {
    font-weight: 400;
  }
  .applications-section .section-title::after,
  .related-section .section-title::after {
    content: '';
  }
  
  /* 大屏幕时相关产品调整：一行6个，图片缩小 */
  .related-grid {
    grid-template-columns: repeat(6, 1fr);
  }
  
  .related-image {
    padding-top: 100%; /* 从200%缩小到正方形 */
  }
}

.related-grid {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 30px 20px;
}

.related-item {
  background: transparent;
  border: none;
  cursor: pointer;
  transition: transform 0.3s ease;
  display: block;
  text-decoration: none;
}

.related-item:hover {
  transform: translateY(-2px);
}

.related-image {
  width: 100%;
  padding-top: 200%;
  position: relative;
  background: #f7f7f7;
  border: 1px solid #ddd;
  overflow: hidden;
}

.related-image img {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.related-item:hover .related-image img {
  transform: scale(1.01);
}

.related-info {
  padding: 12px 0;
  text-align: left;
  background: transparent;
}

.related-name {
  font-size: 16px;
  font-weight: 500;
  color: #333;
  /* font-family: "Lantinghei SC Extralight", "Lantinghei SC Demibold", "Lantinghei SC Heavy", "Lantinghei SC", "PingFang SC", "Microsoft YaHei", "Helvetica Neue", Arial, sans-serif; */
  margin-bottom: 5px;
}

.related-spec {
  font-size: 13px;
  color: #666;
}

@media (min-width: 992px) and (max-width: 1199.98px) {
  .related-grid { grid-template-columns: repeat(6, 1fr); }
  .related-image { padding-top: 100%; }
}
@media (min-width: 1200px) {
  .related-grid { grid-template-columns: repeat(6, 1fr); }

}
@media (min-width: 768px) and (max-width: 991.98px) {
  .related-grid { grid-template-columns: repeat(4, 1fr); }
  .related-image { padding-top: 200%; }
}
@media (max-width: 768px) {
  .related-grid { grid-template-columns: repeat(5, 1fr); gap: 12px 10px; }
  .related-image { padding-top: 200%; }
  .related-info { padding: 8px 0; }
  .related-name { font-size: 13px; }
  .related-spec { font-size: 11px; }
}
@media (max-width: 480px) {
  .related-grid { grid-template-columns: repeat(2, 1fr); gap: 15px 10px; }
  .related-image { padding-top: 200%; }
  .related-info { padding: 8px 0; }
  .related-name { font-size: 13px; }
  .related-spec { font-size: 11px; }
}

/* 图片预览模态框 */
.image-preview-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  position: relative;
  max-width: 90%;
  max-height: 90%;
  text-align: center;
}

.close-btn {
  position: absolute;
  top: -40px;
  right: 0;
  background: none;
  border: none;
  color: #fff;
  font-size: 30px;
  cursor: pointer;
}

.modal-content img {
  max-width: 100%;
  max-height: 80vh;
  object-fit: contain;
}

.preview-title {
  color: #fff;
  margin-top: 15px;
  font-size: 18px;
}

@media (min-width: 992px) {
  .preview-title {
    /* font-family: "Lantinghei SC Extralight", "Lantinghei SC Demibold", "Lantinghei SC Heavy", "Lantinghei SC", "PingFang SC", "Microsoft YaHei", "Helvetica Neue", Arial, sans-serif; */
  }
}

/* 响应式设计 */
@media (max-width: 1024px) {
  .product-layout {
    grid-template-columns: 120px 350px 300px;
    row-gap: 0px;
    column-gap: 30px;
  }
  
  .product-info { height: 100%; }
  .applications-grid {
    width: 100%;
  }
  
  .application-item img {
    height: 700px;
  }
}

/* 平板：两列布局（左侧缩略 + 主图），参数信息整块置于下方，占满两列 */
@media (min-width: 768px) and (max-width: 991.98px) {
  .product-layout {
    grid-template-columns: 100px 1fr;
    column-gap: 20px;
    row-gap: 20px;
  }
  .styles-gallery { grid-column: 1; grid-row: 1; }
  .main-image { grid-column: 2; grid-row: 1; }
  .product-info { grid-column: 1 / -1; grid-row: 2; height: auto; }
}

@media (max-width: 768px) {
  .product-layout {
    grid-template-columns: 84px minmax(200px, 30vw);
    justify-content: center;
    column-gap: 12px;
    row-gap: 16px;
  }
  .styles-gallery { grid-column: 1 / -1; grid-row: 1; position: relative; }
  .main-image { display: none; }
  .product-info { 
    grid-column: 1 / -1; 
    grid-row: 2; 
    height: auto; 
    display: block;
    margin-top: 45px;
    text-align: center;
  }
  .product-name, .product-description, .origin-tag { text-align: center; margin-left: auto; margin-right: auto; }
  .spec-list { align-items: center; }
  .spec-item { justify-content: center; }
  .spec-label, .spec-value { text-align: center; }
  
  /* 只显示当前项，配合左右箭头 */
  .styles-list { 
    display: flex;
    flex-direction: row; 
    overflow: hidden; 
    gap: 10px; 
    justify-content: center; /* 居中当前项 */
  }
  .style-item { 
    display: none;
    flex: 0 0 auto; 
    width: 100%; 
    max-width: none; 
    padding-top: 150%; 
  }
  .style-item.active { 
    display: block; 
    width: 70%; /* 更窄 */
    padding-top: 200%; /* 1:2 比例 */
    margin: 0 auto;
  }

  /* 箭头按钮：放在图片两边（外侧），无背景，尺寸x2 */
  .nav-arrow {
    display: block;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    width: 96px;
    height: 96px;
    border: none;
    background: transparent;
    color: #9e9e9e; /* 灰色 */
    font-size: 72px;
    line-height: 96px;
    text-align: center;
    cursor: pointer;
    z-index: 2;
    padding: 0;
    font-weight: 200;
    /* font-family: "Helvetica Neue", Arial, sans-serif; */
    font-variation-settings: "wght" 200;
  }
  .nav-arrow.prev { left: -56px; }
  .nav-arrow.next { right: -56px; }
  
  /* 小屏下产品摘要下方分割线 */
  .product-summary { padding-bottom: 12px; margin-bottom: 16px; border-bottom: 1px solid #e5e5e5; }
  
  /* 去掉小屏下右侧信息的栅格分列，并取消左边分割线 */
  .product-info .section-divider { display: none; }
  .specifications { border-left: none; padding-left: 0; margin-top: 16px; }
  .notes { border-left: none; padding-left: 0; margin-top: 0; }
  .spec-title { display: none; border: none; margin: 0; padding: 0; }
  .spec-item { flex-direction: row; gap: 4px; }
  
  .applications-grid {
    grid-template-columns: 1fr;
    gap: 15px;
    width: 100% !important;
    margin: 0;
    align-items: stretch;
  }
  
  .application-item { width: 100% !important; max-width: none; }
  
  .application-item {
    max-width: 500px;
  }
  
  .application-item img {
    width: 100%;
    height: auto;
    aspect-ratio: 1 / 1; /* 小屏 1:1 比例 */
    object-fit: cover;
  }
  
  .related-grid {
    grid-template-columns: repeat(4, 1fr);
    gap: 16px 14px;
  }
  /* 仅显示前4个 */
  .related-grid .related-item:nth-child(n+5) { display: none; }
  .related-image { width: 86%; margin: 0 auto; }
  .related-info { width: 86%; margin: 0 auto; }
  
  .related-image {
    padding-top: 200%; /* 1:2 比例 */
  }
  
  .related-info {
    padding: 10px 0;
    background: transparent;
  }
  
  .related-name {
    font-size: 20px;
  }
  
  .related-spec {
    font-size: 16px;
  }
  
  .product-name {
    font-size: 48px;
  }
  
  .applications-section .section-title,
  .related-section .section-title {
    font-size: 22px;
    font-weight: 400;
    color: #858585;
  }

  /* 手机端：加大产品信息字体（约1.5x） */
  .origin-tag { font-size: 24px; }
  .product-description { font-size: 22px; }
  .spec-label { font-size: 22px; }
  .spec-value { font-size: 22px; }
  .contact-btn { font-size: 18px; }
  /* 小屏标题居中且去掉冒号 */
  .applications-section .section-title,
  .related-section .section-title {
    text-align: center;
    font-size: 20px;
    font-weight: 400;
    color: #858585;
  }
  .applications-section .section-title::after,
  .related-section .section-title::after {
    content: '';
  }
}

@media (max-width: 480px) {
  .container {
    padding: 0 15px;
  }
  
  .product-section {
    padding: 20px 0;
  }
  
  .applications-section,
  .related-section {
    padding: 40px 0;
  }
  
  .product-info { height: auto; }
  .product-layout { grid-template-columns: 72px minmax(180px, 40vw); justify-content: center; column-gap: 12px; }
  .styles-gallery { grid-column: 1 / -1; grid-row: 1; position: relative; }
  .main-image { display: none; }
  .product-info { grid-column: 1 / -1; grid-row: 2; display: block; margin-top: 45px; text-align: center; }
  .product-name, .product-description, .origin-tag { text-align: center; margin-left: auto; margin-right: auto; }

  .product-name { font-size: 40px; }

  /* 只显示当前项 */
  .styles-list { display: flex; overflow: hidden; justify-content: center; }
  .style-item { display: none; width: 100%; max-width: none; padding-top: 150%; }
  .style-item.active { display: block; width: 75%; padding-top: 200%; margin: 0 auto; }

  /* 箭头按钮：放在图片两边（外侧），无背景，尺寸x2 */
  .nav-arrow { display: block; position: absolute; top: 50%; transform: translateY(-50%); width: 104px; height: 104px; border: none; background: transparent; color: #9e9e9e; font-size: 80px; line-height: 104px; text-align: center; cursor: pointer; z-index: 2; padding: 0; font-weight: 200; font-variation-settings: "wght" 200; }
  /* .nav-arrow { display: block; position: absolute; top: 50%; transform: translateY(-50%); width: 104px; height: 104px; border: none; background: transparent; color: #9e9e9e; font-size: 80px; line-height: 104px; text-align: center; cursor: pointer; z-index: 2; padding: 0; font-weight: 200; font-family: "Helvetica Neue", Arial, sans-serif; font-variation-settings: "wght" 200; } */

  .nav-arrow.prev { left: -8rem; }
  .nav-arrow.next { right: -8rem; }

  .spec-list { align-items: center; }
  .spec-item { justify-content: center; }
  .spec-label, .spec-value { text-align: center; }

  /* 小屏下产品摘要下方分割线 */
  .product-summary { padding-bottom: 12px; margin-bottom: 30px; border-bottom: 1px solid #e5e5e5; }

  .specifications { margin-top: 16px; }
  .spec-list { grid-template-columns: repeat(2, 1fr); }
  /* 保持规格项单行显示 */
  .spec-item { flex-direction: row; gap: 4px; }
  
  .applications-grid {
    grid-template-columns: 1fr;
    gap: 10px;
    width: 100% !important;
    margin: 0;
    align-items: stretch;
  }
  
  .application-item { width: 100% !important; max-width: none; }
  
  /* 相关产品：一行4个，超出隐藏 */
  .related-grid { grid-template-columns: repeat(4, 1fr); gap: 14px 12px; }
  .related-grid .related-item:nth-child(n+5) { display: none; }
  .related-image { width: 84%; margin: 0 auto; padding-top: 200%; } /* 1:2 比例 */
  .related-info { width: 84%; margin: 0 auto; padding: 6px 0; text-align: left; }
  .related-name { font-size: 20px; }
  .related-spec { font-size: 23px; }

  /* 超小屏：进一步加大产品信息字体（约1.6x） */
  .origin-tag { font-size: 26px; }
  .product-description { font-size: 24px; }
  .spec-label { font-size: 24px; }
  .spec-value { font-size: 24px; }
  .contact-btn { font-size: 20px; }
}
</style>
